import { make } from 'vuex-pathify';
import { filter, keys, keyBy, sortBy, values } from 'lodash';

const strategies = keyBy(
    [
        {
            id: 1,
            title: 'Let Go',
            image: require('@/assets/assertiveness/assertiveness_letGo.png'),
            text:
                'Say “Let it go” to yourself as many times as you need. Give it a few seconds or a whole minute. Notice that telling yourself this will make the intense feeling easier to deal with.',
            visited: false,
            scenario1: {
                text: 'Everyone makes mistakes, including your boss! Let it go and move on.',
                selected: false
            },
            scenario2: {
                text: 'Is whatever you and your friend are fighting about worth it? If not, let it go!',
                selected: false
            },
            scenario3: {
                text: 'That person was rude, but it doesn’t have to ruin your day!',
                selected: false
            }
        },
        {
            id: 2,
            title: 'Count to 10',
            image: require('@/assets/assertiveness/assertiveness_count.png'),
            text:
                'It’s just a way of saying wait a while so you’re not so overwhelmed. You might need to count to 10 three or four times, but it really works.',
            visited: false,
            scenario1: {
                text: 'Counting to 10 will give you a moment to compose yourself so you don’t say something you’ll regret.',
                selected: false
            },
            scenario2: {
                text: 'Counting to 10 will give you a moment so you don’t say something you’ll regret.',
                selected: false
            },
            scenario3: {
                text: 'By the time you count to 10, that person will be out of sight, and hopefully out of mind.',
                selected: false
            }
        },
        {
            id: 3,
            title: 'Breathe Deep',
            image: require('@/assets/assertiveness/assertiveness_breatheDeep.png'),
            text:
                'When we get overwhelmed or worked up we sometimes hold our breath, or start taking shallow, short breaths. Here’s what you do: breathe in through your nose until you feel your stomach fill up like a balloon, and then breathe out through your mouth nice and slow.  It helps you think clearly and is a great way to start letting go.',
            visited: false,
            scenario1: {
                text: 'Take a few deep breaths and decide how you want to respond.',
                selected: false
            },
            scenario2: {
                text: 'Take a few deep breaths and decide how you want to respond.',
                selected: false
            },
            scenario3: {
                text: 'Take a few deep breaths to calm yourself.',
                selected: false
            }
        },
        {
            id: 4,
            title: 'Talk About It',
            image: require('@/assets/assertiveness/assertiveness_talkAboutIt.png'),
            text: 'Talk to someone you trust. A friend you respect, a counselor, a teacher, a doctor, a caseworker, or therapist.',
            visited: false,
            scenario1: {
                text: 'Talking it over with a friend or co-worker later can help you process your feelings.',
                selected: false
            },
            scenario2: {
                text: 'Consider telling your friend you’re going through a tough time and asking for support.',
                selected: false
            },
            scenario3: {
                text: 'Complain about that rude AF person to a friend or family member!',
                selected: false
            }
        },
        {
            id: 5,
            title: 'Distract Yourself',
            image: require('@/assets/assertiveness/assertiveness_distractYourself.png'),
            text:
                'Do something to take your mind off the strong, overwhelming feeling.  Post a selfie of an “opposite feeling” (like happy, low-key, or fierce), scroll your favorite Instagram feed, take a walk, exercise, listen to some music, write out your feelings in a journal, watch your favorite show.  When you return to the issue you’ll be calmer and ready to make a decision.',
            visited: false,
            scenario1: {
                text:
                    'This one might be hard to do at work, but if you still feel bad when you get off work, distract yourself with something fun and positive!',
                selected: false
            },
            scenario2: {
                text: 'Distract yourself with some fun things to get your mind off of things.',
                selected: false
            },
            scenario3: {
                text: 'Scroll through Instagram or listen to music to get your mind off of it.',
                selected: false
            }
        },
        {
            id: 6,
            title: 'Think Positive',
            image: require('@/assets/assertiveness/assertiveness_thinkPositive.png'),
            text: 'Tell yourself how strong you are and how much you’ve survived.  This too shall pass.',
            visited: false,
            scenario1: {
                text: 'This sucks, but focus on the positive—you have a job, and you are doing great.',
                selected: false
            },
            scenario2: {
                text: 'You are so strong and you can overcome whatever is going on!',
                selected: false
            },
            scenario3: {
                text: 'Hey, that person is rude, but you are not!',
                selected: false
            }
        },
        {
            id: 7,
            title: 'Walk Away',
            image: require('@/assets/assertiveness/assertiveness_walkAway.png'),
            text:
                'This may be the hardest of all, but sometimes walking away is the best thing you can do.  Some people are just ignorant and don’t deserve your time or attention. Walking away with your head held high preserves your self-respect and pride – you haven’t stooped to their level. Keep in mind that there are some people you probably shouldn’t walk away from, like the police for example.',
            visited: false,
            scenario1: {
                text: 'If you walk out, you might get fired! But  maybe you can find a way to take a minute for yourself.',
                selected: false
            },
            scenario2: {
                text: 'It’s hard to walk away from your best friend, but maybe you can take some time and talk later when you’re feeling better.',
                selected: false
            },
            scenario3: {
                text: 'Walk away from that rude AF person before you say something you’ll regret later!',
                selected: false
            }
        }
    ],
    'id'
);
const strategiesOrder = sortBy(keys(strategies), k => parseInt(k));

const state = {
    title: 'Assertiveness Under Pressure',
    complete: false,
    sticker: {
        text: 'Cloud',
        earned: false,
        image: require('@/assets/stickers/stickers_0014_cloud.png')
    },
    bonusSticker: false,
    trophy: false,
    strategies,
    strategiesOrder,
    myStrategy: '',
    scenario1Text: 'Your boss yelled at you for making a mistake at work, and you just want to quit right then and there.',
    scenario2Text:
        'You are feeling really overwhelmed about some of the things that aren’t going great in your life. Then you get in a fight with your best friend and you just want to tell them off.',
    scenario3Text:
        'You are getting off a bus, and someone behind you rudely pushes you and says, “Out of my way, you’re moving too slow!” You want to shove them back and call them a few choice names!'
};
const getters = {
    ...make.getters(state),
    allStrategiesVisited: state => {
        return 0 === filter(values(state.strategies), { visited: false }).length;
    },
    scenario1Selected: state => {
        return filter(values(state.strategies), { scenario1: { selected: true } }).length;
    },
    scenario2Selected: state => {
        return filter(values(state.strategies), { scenario2: { selected: true } }).length;
    },
    scenario3Selected: state => {
        return filter(values(state.strategies), { scenario3: { selected: true } }).length;
    }
};
const mutations = make.mutations(state);
const actions = make.actions(state);

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
